/** The logged-in learner (from the API; the App never stores credentials itself). */
import { useQuery, type QueryClient } from '@tanstack/react-query';
import { auth, type Me } from '../api/endpoints.ts';
import { isApiError } from '../api/http.ts';

export const ME_KEY = ['me'] as const;

export function useMe() {
  return useQuery<Me>({
    queryKey: ME_KEY,
    queryFn: auth.me,
    staleTime: 60_000,
    // "Not logged in" is an answer, not a failure to retry.
    retry: (count, error) => !isApiError(error) && count < 2,
  });
}

/** After the session ends (logout, expiry): forget everything cached for that learner. */
export function forgetSession(client: QueryClient): void {
  client.clear();
}

/**
 * Where to go after logging in. Only paths of this App are allowed: `?next=` comes from the
 * URL, so it must not be able to send the learner to another site (open redirect).
 */
export function safeNext(next: string | null, fallback = '/'): string {
  if (next === null || !next.startsWith('/') || next.startsWith('//') || next.includes('\\')) {
    return fallback;
  }
  try {
    const base = 'https://app.invalid';
    const url = new URL(next, base);
    if (url.origin !== base) return fallback;
    const path = `${url.pathname}${url.search}${url.hash}`;
    return path.startsWith('/login') || path.startsWith('/admin/login') ? fallback : path;
  } catch {
    return fallback;
  }
}

/** Where each kind of account starts (CS staff manage accounts only, ADR-0009). */
export function homeOf(role: Me['role']): string {
  return role === 'admin' ? '/admin' : role === 'staff' ? '/admin/accounts' : '/';
}

export function loginPath(next: string, admin = false): string {
  return `${admin ? '/admin/login' : '/login'}?next=${encodeURIComponent(next)}`;
}
